import React, { useEffect } from 'react'
import {
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
  UseFormClearErrors,
  Path,
  Controller,
  useFieldArray
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Icon,
  Select,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import moment from 'moment'
import { calculateTotalValue } from 'templates/Charge/sharedCharge.utils'
import { v4 as uuidv4 } from 'uuid'

import { DatePicker } from 'components/DatePicker'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'

import { formatCentsToSendToBackEnd } from 'utils'
import { stringToDate } from 'utils/FormatDate'
import { onlyNumber, formatMoney, priceConverter } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import { days as daysMap } from '../../../Discounts/Discounts.utils'

// Cycle options for discount (similar to bonification)
export const discountCyclesOptions = [
  { label: 'Todos os próximos', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 },
  { label: '11', value: 11 },
  { label: '12', value: 12 },
  { label: 'Outros', value: 13 }
]

// Interfaces - Estrutura simplificada sem gerenciamento manual de erros
export interface DiscountField {
  amount?: string | undefined
  type?: string
  date?: Date | undefined
  days?: string | undefined
  totalCycles?: number
  totalCyclesCustom?: string
  startCycle?: number
}

export interface DiscountsProps<T extends FieldValues> {
  control: Control<T, object>
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  clearErrors: UseFormClearErrors<T>
  recurrence?: boolean
  isEditingRecurrence?: boolean
  isProductOrLink?: boolean
  externalReplaceDiscounts?: (discounts: any[]) => void
}

// Componente interno refatorado para usar useFieldArray
const DiscountsLogic = <T extends FieldValues>({
  control,
  watch,
  setValue,
  clearErrors,
  isRecurrence = false,
  isProductOrLink = false,
  externalReplaceDiscounts
}: {
  control: Control<T, object>
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  clearErrors: UseFormClearErrors<T>
  isRecurrence?: boolean
  isProductOrLink?: boolean
  externalReplaceDiscounts?: (discounts: any[]) => void
}) => {
  const watchedDiscounts =
    (watch('discounts' as Path<T>) as DiscountField[]) || []
  const items = watch(`items` as Path<T>)
  const discountPolicy = watch('discountPolicy' as Path<T>) as boolean
  const isSmall = useMediaQuery('(max-width:600px)')

  // useFieldArray interno para gerenciar discounts
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'discounts' as any
  })

  // Para payment links, usar dados do formulário; para charges/recurrences, sincronizar useFieldArray com formulário
  const fieldsToRender = externalReplaceDiscounts ? watchedDiscounts : fields

  // Sincronizar useFieldArray interno com dados do formulário (para charges/recurrences)
  useEffect(() => {
    if (
      !externalReplaceDiscounts &&
      watchedDiscounts.length > 0 &&
      fields.length !== watchedDiscounts.length
    ) {
      replace(watchedDiscounts as any)
    }
  }, [watchedDiscounts, fields.length, replace, externalReplaceDiscounts])
  const formAmount = watch('amount' as Path<T>)
  // `items` nunca é indefinido: sharedInitialFormValues já traz um item vazio, então a
  // presença dele não diz de onde vem o valor. Cobranças, recorrências e links somam a
  // composição via calculateTotalValue (mesmo helper usado no submit). O form de
  // produtos esconde a composição e o item default fica zerado — lá o valor do plano
  // vem do campo `amount`, que só existe nesse formulário.
  const itemsTotal = items?.length ? calculateTotalValue(items) : 0
  const amount = itemsTotal > 0 ? itemsTotal : priceConverter(formAmount)
  const dueDate = watch('dueDate' as Path<T>) as string

  // Função de validação de valor para usar nas rules dos Controllers
  const validateAmount = (value: string, currentType: string) => {
    // Se discountPolicy estiver desabilitado, não validar
    if (!discountPolicy) return true

    if (!value || value.trim() === '') {
      return 'Campo obrigatório.'
    }

    const isPercent = currentType === '0'
    let numericValue: number

    if (isPercent) {
      // Para porcentagem, usar valor direto
      numericValue = Number(value)

      if (isNaN(numericValue) || numericValue <= 0) {
        return 'A porcentagem deve ser maior que 0%'
      }

      if (numericValue > 90) {
        return 'A porcentagem do desconto deve ser menor ou igual a 90%.'
      }
    } else {
      // Para valores monetários, o valor já vem formatado do onChange
      try {
        // O valor já vem formatado como "12.00" do onChange, usar diretamente
        numericValue = Number(value)

        if (isNaN(numericValue) || numericValue <= 0) {
          return 'O valor deve ser maior que 0.'
        }

        // amount já vem em reais, normalizado por priceConverter
        const totalAmountInReais = amount

        if (totalAmountInReais > 0) {
          if (numericValue > totalAmountInReais) {
            return 'O valor do desconto deve ser menor que o da fatura.'
          }
          if (numericValue > totalAmountInReais * 0.9) {
            return 'O valor do desconto deve ser menor ou igual a 90% do valor total.'
          }
        }
      } catch (error) {
        return 'Valor inválido.'
      }
    }

    return true // ✅ Retorna true quando válido
  }

  // Função de validação de data para usar nas rules dos Controllers
  const validateDate = (value: Date) => {
    // Se discountPolicy estiver desabilitado, não validar
    if (!discountPolicy) return true

    if (!value) {
      return 'Campo obrigatório.'
    }

    let dueDateLocal = dueDate as string | Date | undefined
    if (typeof dueDateLocal === 'string') {
      dueDateLocal = stringToDate(dueDate as string)
    }

    const billingDueDate = moment(dueDateLocal as Date)
    const currentDate = moment(value)
    const todayDate = moment()

    if (!currentDate.isValid()) {
      return 'Deve ser uma data válida.'
    }

    if (currentDate.isBefore(todayDate, 'day')) {
      return 'A data deve ser maior ou igual a data atual.'
    }

    if (
      billingDueDate.isValid() &&
      currentDate.isAfter(billingDueDate, 'day')
    ) {
      return 'A data deve ser igual ou anterior a data de vencimento.'
    }

    return true // ✅ Retorna true quando válido
  }

  // Função para validar duplicidade de datas
  const validateDuplicateDate = (value: Date, index: number) => {
    // Se discountPolicy estiver desabilitado, não validar
    if (!discountPolicy) return true
    if (!value) return true

    const currentDiscounts = watch('discounts' as Path<T>) as DiscountField[]
    if (!currentDiscounts || currentDiscounts.length <= 1) return true

    const duplicateFound = currentDiscounts.some((discount, i) => {
      if (i === index || !discount.date) return false
      return (
        moment(discount.date).format('DD/MM/YYYY') ===
        moment(value).format('DD/MM/YYYY')
      )
    })

    return duplicateFound
      ? 'O dia do desconto deve ser diferente do dia já selecionado.'
      : true
  }

  // 0 é "O dia do vencimento": vazio é só undefined/null/'' (SC-11660)
  const hasDays = (value: unknown) =>
    value !== undefined && value !== null && value !== ''

  // Função para validar duplicidade de dias (recorrência)
  const validateDuplicateDays = (value: string, index: number) => {
    // Se discountPolicy estiver desabilitado, não validar
    if (!discountPolicy) return true
    if (!hasDays(value)) return true

    const currentDiscounts = watch('discounts' as Path<T>) as DiscountField[]
    if (!currentDiscounts || currentDiscounts.length <= 1) return true

    const duplicateFound = currentDiscounts.some((discount, i) => {
      if (i === index || !hasDays(discount.days)) return false
      return String(discount.days) === String(value)
    })

    return duplicateFound
      ? 'O dia do desconto deve ser diferente do dia já selecionado.'
      : true
  }

  // Função para revalidar todos os campos de desconto
  const revalidateDiscounts = () => {
    watchedDiscounts.forEach((discount, index) => {
      // Revalidar campo de valor se existir
      if (discount.amount) {
        const currentType = discount.type || '1'
        const validationResult = validateAmount(discount.amount, currentType)

        if (validationResult !== true) {
          control.setError(`discounts.${index}.amount` as Path<T>, {
            type: 'validate',
            message: validationResult
          })
        }
      }

      // Revalidar campo de data se existir (cobrança)
      if (!isRecurrence && discount.date) {
        const dateValidation = validateDate(discount.date)
        const duplicateValidation = validateDuplicateDate(discount.date, index)

        let errorMessage = ''
        if (dateValidation !== true) {
          errorMessage = dateValidation
        } else if (duplicateValidation !== true) {
          errorMessage = duplicateValidation
        }

        if (errorMessage) {
          control.setError(`discounts.${index}.date` as Path<T>, {
            type: 'validate',
            message: errorMessage
          })
        } else {
          // Limpar erro quando validação passa
          clearErrors(`discounts.${index}.date` as Path<T>)
        }
      }

      // Revalidar campo de dias se existir (recorrência)
      if (isRecurrence && hasDays(discount.days)) {
        const duplicateValidation = validateDuplicateDays(
          discount.days as string,
          index
        )

        if (duplicateValidation !== true) {
          control.setError(`discounts.${index}.days` as Path<T>, {
            type: 'validate',
            message: duplicateValidation
          })
        } else {
          // Limpar erro quando validação passa
          clearErrors(`discounts.${index}.days` as Path<T>)
        }
      }
    })
  }

  // useEffect para revalidar quando valores importantes mudam
  useEffect(() => {
    if (discountPolicy && watchedDiscounts.length > 0) {
      // Pequeno delay para garantir que o DOM foi atualizado
      const timeoutId = setTimeout(() => {
        revalidateDiscounts()
      }, 100)

      return () => clearTimeout(timeoutId)
    }

    return undefined
  }, [amount, dueDate, watchedDiscounts, discountPolicy])

  // Ao ligar o desconto já traz uma linha pra preencher, em vez de abrir vazio
  // e obrigar a clicar em "Adicionar desconto" (cobrança, recorrência, plano e link)
  useEffect(() => {
    if (
      discountPolicy &&
      fieldsToRender.length === 0 &&
      watchedDiscounts.length === 0 // Só adicionar se não há dados no formulário também
    ) {
      append({
        uuid: uuidv4(),
        amount: '',
        days: undefined,
        date: undefined,
        type: '1',
        errors: undefined
      } as any)
    }
  }, [discountPolicy, fieldsToRender.length, append, watchedDiscounts.length])

  // Função para deletar desconto usando remove do useFieldArray
  const deleteDiscount = (index: number) => {
    remove(index)
  }

  // Limite máximo de descontos é sempre 2
  const maxDiscounts = 2

  // Helper: calcula o valor com desconto para a linha atual
  const getDiscountedAmountForIndex = (idx: number) => {
    const total = amount
    const d = (watch(`discounts.${idx}` as Path<T>) as DiscountField) || {}
    const t = d.type || '1'
    const v = d.amount
    if (!v || String(v).trim() === '') return total

    let discount = 0
    if (t === '0') {
      const percent = Number(v)
      if (!isNaN(percent)) discount = total * (percent / 100)
    } else {
      const money = Number(v)
      if (!isNaN(money)) discount = money
    }

    const result = Math.max(total - discount, 0)
    return result
  }

  return (
    <Stack spacing={1}>
      {fieldsToRender.map((field, index) => {
        const fieldData =
          (watch(`discounts.${index}` as Path<T>) as DiscountField) || {}
        const { type = '1' } = fieldData

        return (
          <>
            <Box
              key={(field as any).id || (field as any).uuid || index}
              sx={{
                display: 'grid',
                gap: '10px',
                gridTemplateColumns: isSmall ? '1fr' : '1fr 1fr 1fr 40px',
                alignItems: 'center'
              }}
            >
              {/* Campo Tipo - Refatorado para usar Controller com validação centralizada */}
              <Controller
                name={`discounts.${index}.type` as Path<T>}
                control={control}
                defaultValue={'1' as any}
                render={({ field: controllerField, fieldState: { error } }) => (
                  <Box sx={{ position: 'relative' }}>
                    <Select
                      label={'Tipo'}
                      data-cy={`discount-type-${index}`}
                      value={
                        controllerField.value === '1' ? 'valor' : 'porcentagem'
                      }
                      onChange={(event) => {
                        const newType =
                          event.target.value === 'valor' ? '1' : '0'
                        const currentValue = watch(
                          `discounts.${index}.amount` as Path<T>
                        )

                        controllerField.onChange(newType)

                        if (currentValue) {
                          setValue(
                            `discounts.${index}.amount` as Path<T>,
                            '' as any,
                            {
                              shouldValidate: false
                            }
                          )
                        }
                      }}
                      helperText={isSmall ? error?.message || '' : ''}
                      error={!!error?.message}
                      sx={{
                        '& .MuiFormHelperText-root': {
                          minHeight: '20px'
                        }
                      }}
                    >
                      <MenuItem value="valor">Valor</MenuItem>
                      <MenuItem value="porcentagem">Porcentagem</MenuItem>
                    </Select>
                    {!isSmall && error?.message && (
                      <Typography
                        variant="xsLight"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          bottom: 0,
                          color: (theme) => theme.palette.error.main
                        }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              {/* Campo Valor/Porcentagem - Refatorado com validação centralizada */}
              <Controller
                control={control}
                name={`discounts.${index}.amount` as Path<T>}
                rules={{
                  validate: (value) => {
                    // Validação imediata quando o usuário digita
                    const currentType =
                      (watch(`discounts.${index}.type` as Path<T>) as string) ||
                      '1'
                    return validateAmount(value, currentType)
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={{ position: 'relative' }}>
                    <Textfield
                      {...field}
                      label={type === '1' ? 'Valor' : 'Porcentagem'}
                      placeholder={type === '1' ? 'R$' : '%'}
                      disabled={!discountPolicy}
                      helperText={isSmall ? error?.message || '' : ''}
                      data-cy={`discount-amount-${index}`}
                      {...(type === '0' && {
                        InputProps: {
                          inputComponent: NumberFormatCustom as any
                        }
                      })}
                      error={!!error?.message}
                      hiddenLabel
                      mask={type === '0' ? 'string' : 'money'}
                      onChange={(event) => {
                        const formattedValue =
                          type === '1'
                            ? formatCentsToSendToBackEnd(
                                onlyNumber(event.target.value)
                              )
                            : event.target.value
                        field.onChange(formattedValue)
                        setTimeout(() => {
                          const currentType =
                            (watch(
                              `discounts.${index}.type` as Path<T>
                            ) as string) || '1'
                          const validationResult = validateAmount(
                            formattedValue,
                            currentType
                          )
                          if (validationResult !== true) {
                            control.setError(
                              `discounts.${index}.amount` as Path<T>,
                              {
                                type: 'validate',
                                message: validationResult
                              }
                            )
                          }
                        }, 0)
                      }}
                      sx={{
                        '& .MuiFormHelperText-root': {
                          minHeight: '20px'
                        }
                      }}
                    />
                    {!isSmall && error?.message && (
                      <Typography
                        variant="xsLight"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          bottom: -28,
                          color: (theme) => theme.palette.error.main
                        }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              {/* Campo Data/Dias - Refatorado com validação centralizada */}
              {/* Para produtos/links sempre usar dias, para cobranças usar data, para recorrências usar dias */}
              {!isRecurrence && !isProductOrLink ? (
                <Controller
                  control={control}
                  name={`discounts.${index}.date` as Path<T>}
                  rules={{
                    validate: (value) => {
                      // Validação imediata quando o usuário seleciona data
                      const dateError = validateDate(value)
                      if (dateError !== true) return dateError

                      return validateDuplicateDate(value, index)
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ position: 'relative' }}>
                      <DatePicker
                        {...field}
                        disabled={!discountPolicy}
                        helperText={isSmall ? error?.message || '' : ''}
                        error={!!error?.message}
                        label={'Data limite'}
                        maxDate={
                          new Date(moment(dueDate || new Date()).format())
                        }
                        minDate={new Date()}
                        onChange={(newValue) => {
                          field.onChange(newValue)

                          setTimeout(() => {
                            if (newValue) {
                              const dateValidation = validateDate(newValue)
                              const duplicateValidation = validateDuplicateDate(
                                newValue,
                                index
                              )

                              let errorMessage = ''
                              if (dateValidation !== true) {
                                errorMessage = dateValidation
                              } else if (duplicateValidation !== true) {
                                errorMessage = duplicateValidation
                              }

                              if (errorMessage) {
                                control.setError(
                                  `discounts.${index}.date` as Path<T>,
                                  {
                                    type: 'validate',
                                    message: errorMessage
                                  }
                                )
                              } else {
                                clearErrors(
                                  `discounts.${index}.date` as Path<T>
                                )
                              }
                            }
                          }, 0)
                        }}
                        renderInput={(props: any) => (
                          <Textfield
                            {...props}
                            sx={{
                              '& .MuiFormHelperText-root': {
                                minHeight: '20px'
                              }
                            }}
                            data-cy={`discount-date-${index}`}
                          />
                        )}
                        value={field.value || null}
                      />
                      {!isSmall && error?.message && (
                        <Typography
                          variant="xsLight"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                            color: (theme) => theme.palette.error.main
                          }}
                        >
                          {error.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name={`discounts.${index}.days` as Path<T>}
                  rules={{
                    validate: (value) => {
                      // Validação imediata quando o usuário seleciona dia
                      if (!discountPolicy) return true
                      if (!hasDays(value)) return 'Campo obrigatório.'
                      return validateDuplicateDays(value, index)
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ position: 'relative' }}>
                      <Select
                        {...field}
                        helperText={isSmall ? error?.message || '' : ''}
                        error={!!error?.message}
                        fullWidth={true}
                        label={'Desconto válido até'}
                        data-cy={`discount-days-${index}`}
                        value={
                          field.value !== undefined && field.value !== null
                            ? Number(field.value)
                            : ''
                        }
                        onChange={(event) => {
                          field.onChange(event.target.value)

                          setTimeout(() => {
                            const duplicateValidation = validateDuplicateDays(
                              event.target.value as string,
                              index
                            )

                            if (duplicateValidation !== true) {
                              control.setError(
                                `discounts.${index}.days` as Path<T>,
                                {
                                  type: 'validate',
                                  message: duplicateValidation
                                }
                              )
                            } else {
                              clearErrors(`discounts.${index}.days` as Path<T>)
                            }
                          }, 0)
                        }}
                        sx={{
                          '& .MuiFormHelperText-root': {
                            minHeight: '20px'
                          }
                        }}
                      >
                        {daysMap.map(({ label, value }, mapIndex) => (
                          <MenuItem key={mapIndex} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                      {!isSmall && error?.message && (
                        <Typography
                          variant="xsLight"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            bottom: -28,
                            color: (theme) => theme.palette.error.main
                          }}
                        >
                          {error.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              )}

              {/* Botão Delete - Refatorado para usar remove do useFieldArray */}
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  pt: index === 0 && !isSmall ? 3 : 0
                }}
              >
                {isSmall ? (
                  <Button
                    onClick={() => deleteDiscount(index)}
                    variantButton="neutral"
                    startIcon="close"
                    fullWidth
                  >
                    Remover desconto
                  </Button>
                ) : (
                  <IconButton onClick={() => deleteDiscount(index)}>
                    <Icon icon="close" colorSX="#777877" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Typography
              variant="smLight"
              color={theme.palette.neutral[400]}
              sx={{ mt: 0.5 }}
            >
              Valor com desconto:{' '}
              {formatMoney(getDiscountedAmountForIndex(index))}
            </Typography>
          </>
        )
      })}

      {/* Botão Adicionar */}
      {fieldsToRender.length < maxDiscounts && (
        <>
          <Box
            sx={{
              height: '1px',
              width: '100%',
              bgcolor: theme.palette.neutral[700]
            }}
          ></Box>

          <Button
            onClick={() => {
              append({
                amount: undefined,
                type: '1',
                date: undefined,
                days: undefined
              } as any)
            }}
            variantButton="ghost"
            sx={{ width: 'fit-content', mt: '10px' }}
          >
            <Stack direction="row" gap="5px" alignItems="center">
              <Icon
                icon="add"
                sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
              />
              <Typography
                sx={{
                  color: theme.palette.neutral[400],
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Adicionar desconto
              </Typography>
            </Stack>
          </Button>
        </>
      )}

      {/* Configuração avançada de ciclos - apenas para recorrências e produtos/links */}
      {(isRecurrence || isProductOrLink) && fieldsToRender.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{
            mt: 2,
            boxShadow: 'none',
            border: `1px solid ${theme.palette.neutral[700]}`,
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: '16px 0 0 0' }
          }}
        >
          <AccordionSummary
            expandIcon={
              <Icon
                icon="keyboardArrowDown"
                colorSX={theme.palette.neutral[400]}
              />
            }
            sx={{
              minHeight: '48px',
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Icon
                icon="settings"
                colorSX={theme.palette.neutral[400]}
                sx={{ fontSize: '18px' }}
              />
              <Typography
                variant="smBold"
                sx={{ color: theme.palette.neutral[300] }}
              >
                Configuração avançada de ciclos
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gap: '10px',
                gridTemplateColumns: isSmall
                  ? '1fr'
                  : watch('discountTotalCycles' as Path<T>) === 13
                  ? '1fr 1fr 1fr'
                  : '1fr 1fr',
                alignItems: 'start'
              }}
            >
              {/* Ciclo Inicial Global */}
              <Controller
                control={control}
                name={'discountStartCycle' as Path<T>}
                defaultValue={1 as any}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={{ position: 'relative', mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5
                      }}
                    >
                      <Typography variant="smLight">
                        A partir de qual ciclo?
                      </Typography>
                      <Tooltip
                        enterTouchDelay={0}
                        arrow
                        placement="top"
                        title="A partir de qual ciclo da recorrência o desconto começará a valer."
                      >
                        <Box
                          component="span"
                          sx={{ cursor: 'help', display: 'flex' }}
                        >
                          <Icon
                            icon="info"
                            sx={{
                              fontSize: '14px',
                              color: theme.palette.neutral[500]
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Textfield
                      {...field}
                      type="number"
                      inputProps={{ min: 1 }}
                      placeholder="1"
                      value={field.value || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10)
                        if (value >= 1 || e.target.value === '') {
                          field.onChange(value || 1)
                        }
                      }}
                      helperText={isSmall ? error?.message : ''}
                      error={!!error?.message}
                    />
                    {!isSmall && error?.message && (
                      <Typography
                        variant="xsLight"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          bottom: -28,
                          color: (theme) => theme.palette.error.main
                        }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              {/* Quantidade de Ciclos Global - Select */}
              <Controller
                control={control}
                name={'discountTotalCycles' as Path<T>}
                defaultValue={0 as any}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={{ position: 'relative', mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5
                      }}
                    >
                      <Typography variant="smLight">
                        Por quantos ciclos?
                      </Typography>
                      <Tooltip
                        enterTouchDelay={0}
                        arrow
                        placement="top"
                        title="Por quantos ciclos será aplicado o desconto."
                      >
                        <Box
                          component="span"
                          sx={{ cursor: 'help', display: 'flex' }}
                        >
                          <Icon
                            icon="info"
                            sx={{
                              fontSize: '14px',
                              color: theme.palette.neutral[500]
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Select
                      helperText={isSmall ? error?.message || '' : ''}
                      error={!!error?.message}
                      fullWidth
                      value={field.value ?? 0}
                      onChange={(event) => {
                        field.onChange(event.target.value)
                        // If "Outros" is selected, initialize custom field
                        if (event.target.value === 13) {
                          setValue(
                            'discountTotalCyclesCustom' as Path<T>,
                            '' as any
                          )
                        }
                      }}
                    >
                      {discountCyclesOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {!isSmall && error?.message && (
                      <Typography
                        variant="xsLight"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          bottom: -28,
                          color: (theme) => theme.palette.error.main
                        }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              {/* Custom Cycles Input Global (when "Outros" is selected) */}
              {watch('discountTotalCycles' as Path<T>) === 13 && (
                <Controller
                  control={control}
                  name={'discountTotalCyclesCustom' as Path<T>}
                  render={({ field, fieldState: { error } }) => (
                    <Box sx={{ position: 'relative' }}>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="smLight" sx={{ fontSize: '13px' }}>
                          Qtd.
                        </Typography>
                      </Box>
                      <Textfield
                        helperText={isSmall ? error?.message || '' : ''}
                        onChange={(event) => {
                          const res = onlyNumber(event.target.value)
                          if (Number(res) <= 120) {
                            field.onChange(res)
                          }
                        }}
                        value={field.value || ''}
                        placeholder="Qtd."
                        error={!!error?.message}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: '14px'
                          }
                        }}
                      />
                      {!isSmall && error?.message && (
                        <Typography
                          variant="xsLight"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            bottom: -28,
                            color: (theme) => theme.palette.error.main
                          }}
                        >
                          {error.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  )
}

// Componente principal refatorado - Props simplificadas
const Discounts = <T extends FieldValues>({
  control,
  watch,
  setValue,
  clearErrors,
  recurrence,
  isEditingRecurrence,
  isProductOrLink = false,
  externalReplaceDiscounts
}: DiscountsProps<T>) => {
  return (
    <Controller
      control={control}
      name={'discountPolicy' as Path<T>}
      render={({ field }) => (
        <Accordion
          expanded={field.value}
          onChange={(_, expanded) =>
            setValue('discountPolicy' as Path<T>, expanded as any)
          }
          sx={{
            mt: 2,
            border: '1px solid #E8EBE8',
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            borderRadius: '8px !important',
            backgroundColor: '#FAFBFA',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: '16px 0 0 0',
              backgroundColor: '#fff'
            }
          }}
        >
          <AccordionSummary
            hasSwitch
            expandIcon={<Switch checked={field.value} {...field} />}
          >
            <Typography variant="smBold">Desconto</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <DiscountsLogic
              control={control as any}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              isRecurrence={recurrence || isEditingRecurrence}
              isProductOrLink={isProductOrLink}
              externalReplaceDiscounts={externalReplaceDiscounts}
            />
          </AccordionDetails>
        </Accordion>
      )}
    />
  )
}

export default Discounts
